import React, { FC } from "react";
import { alpha, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppMenuKey } from "../../../../../../../../common/routesUtils";
import { useCurrentTheme } from "../../../../../../../../theme/theme";
import { MenuIcon } from "../../../MenuIcon";

/**
 * Selection state of a section item:
 * - "active":   the item is the current page (or hides it), rendered as a pill;
 * - "ancestor": the current page is a visible child of the item, rendered with a lighter tint of the pill.
 */
export type SectionItemState = "active" | "ancestor";

interface Props {
	disabled?: boolean;
	expand?: "more" | "less";
	itemKey: AppMenuKey;
	menuIsOpen: boolean;
	state?: SectionItemState;
	onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

/**
 * SectionItem renders a single item of the application side menu which
 * represents an application section.
 *
 * @param disabled   if true, renders the item as disabled; a single page item (no expand) is also
 *                   not clickable, while an expandable section can still be opened to show its sub-items
 * @param expand     if define, controls the "expand" / "collapse" chevron rendered
 *                   after the section name
 * @param itemKey    the menu key representing the section
 * @param menuIsOpen this flag is used to correctly render the menu item
 * @param state      the selection state of the item ("active", "ancestor" or undefined)
 * @param onClick    the handler of the "on click" event that may be triggered by users interaction
 * @returns          an item of the application side menu
 */
const SectionItem: FC<Props> = ({ disabled = false, expand, itemKey, menuIsOpen, state, onClick }) => {
	const { t } = useTranslation("navigation");
	const { theme } = useCurrentTheme();
	const title = t([`breadcrumbs/${itemKey}`, `breadcrumbs/<${itemKey}>`]);

	return (
		<ListItem disablePadding>
			<ListItemButton
				selected={state === "active"}
				disabled={disabled && !expand}
				onClick={(e) => onClick(e)}
				sx={{
					minHeight: 48,
					justifyContent: "center",
					mx: 1, // margin + reduced padding keep the icon in the same position
					my: 0.25,
					px: 1,
					borderRadius: 2,
					"&.Mui-selected, &.Mui-selected:hover": {
						bgcolor: "action.selected",
						color: "primary.main",
					},
					// disabled look for expandable sections, which must stay clickable to show their sub-items
					...(disabled &&
						expand && {
							"& .MuiListItemIcon-root, & .MuiListItemText-root": {
								opacity: theme.palette.action.disabledOpacity,
							},
						}),
					// same tint as the selected pill, at half its opacity
					...(state === "ancestor" && {
						bgcolor: alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity / 2),
						"&:hover": {
							bgcolor: alpha(theme.palette.action.selected, theme.palette.action.selectedOpacity * 0.75),
						},
					}),
				}}
			>
				{menuIsOpen && (
					<ListItemIcon sx={{ minWidth: "auto" }}>
						<MenuIcon target={itemKey} />
					</ListItemIcon>
				)}

				{!menuIsOpen && (
					<Tooltip arrow title={title} placement={"right"}>
						<ListItemIcon sx={{ minWidth: "auto" }}>
							<MenuIcon target={itemKey} />
						</ListItemIcon>
					</Tooltip>
				)}

				{menuIsOpen && (
					<>
						<ListItemText
							sx={{
								textAlign: "start",
								pl: 1,
								whiteSpace: "pre-line",
							}}
							slotProps={{ primary: { lineHeight: 1.1 } }}
							primary={title}
						/>
						{expand && (
							<ListItemIcon
								sx={{
									justifyContent: "end",
								}}
							>
								<MenuIcon
									sx={{
										justifyContent: "end",
										textAlign: "center",
										transition: "transform .125s",
										transform: expand === "less" ? "rotate(180deg)" : "rotate(0deg)",
										color: theme.palette.primary.main,
									}}
									target={AppMenuKey.ArrowDown}
								/>
							</ListItemIcon>
						)}
					</>
				)}
			</ListItemButton>
		</ListItem>
	);
};

export default SectionItem;
